/**
 * Helpers for POST /api/purchases (plain JSON) and mapping to legacy UI shapes.
 *
 * New contracts (expected):
 * - list: { success:true, message:"radha", data: { items:[], pagination:{} } }
 *   (legacy: data as array or data.purchases)
 * - show: { success:true, message:"radha", data: [ { ...doc, items:[], payments:[] } ] }
 */

import {
  buildStandardPaymentLines,
  mapApiPaymentToUiRow,
  parsePaymentModeFromApi,
} from "./paymentLinesErpAdapter";

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const isRadhaSuccess = (res) =>
  res &&
  (res.success === true ||
    String(res.status || "").toLowerCase() === "success" ||
    res.message === "radha");

export const isPurchasesApiSuccess = isRadhaSuccess;

export const sumPaymentAmounts = (payments) => {
  if (!Array.isArray(payments)) return 0;
  return payments.reduce(
    (s, p) => s + num(p.amount ?? p.amt ?? p.total_amount, 0),
    0,
  );
};

export const remainingFromGrandAndPayments = (grandTotal, payments) => {
  const g = num(grandTotal, 0);
  const paid = sumPaymentAmounts(payments);
  return Math.max(0, g - paid);
};

/**
 * Normalize `payments` from purchase show response for edit UI state.
 * `paymentId` = existing server line id (use for updates). New rows keep `paymentId` null.
 */
export function normalizePurchasePaymentsFromShow(payments) {
  if (!Array.isArray(payments)) return [];
  return payments.map((p, idx) => {
    const row = mapApiPaymentToUiRow(p, idx, { formatAmountFixed2: true });
    return {
      paymentId: row.paymentId,
      paymentIdempotencyKey: row.paymentIdempotencyKey || "",
      bank_account_id: num(row.type, 0),
      amount: num(row.amount, 0),
      rowKey: `pay-${idx}-${row.paymentId ?? "new"}`,
      payment_mode: String(p.payment_mode ?? "").trim(),
      remarks: row.remarks,
      pay_mode: row.pay_mode,
    };
  });
}

/** Map API payment lines to purchase payment row state (Mode / Type / Remarks / Amt). */
export function mapShowPaymentsToPurchasePaymentRows(payments) {
  if (!Array.isArray(payments) || payments.length === 0) return null;
  return payments.map((p, idx) => {
    const row = mapApiPaymentToUiRow(p, idx, { formatAmountFixed2: true });
    const paymentId = row.paymentId;
    return {
      id: paymentId ?? idx + 1,
      paymentId,
      paymentIdempotencyKey: row.paymentIdempotencyKey || "",
      pay_mode: row.pay_mode || parsePaymentModeFromApi(p.payment_mode),
      type: row.type != null && row.type !== "" ? String(row.type) : "",
      remarks: row.remarks || "",
      amount:
        row.amount === "" || row.amount == null
          ? "0.00"
          : row.amount,
      rowKey: `pay-${idx}-${paymentId ?? "new"}`,
      bankDisplayName: row.bankDisplayName,
    };
  });
}

/**
 * Build standard `payments[]` for purchase create/update.
 * Pass `{ isUpdate, createKey, persistKeys, paymentTypes }`.
 */
export function mapPurchasePaymentsForUpdateApi(rows, options = {}) {
  const { createKey, persistKeys, isUpdate = false, paymentTypes = [] } =
    options;
  return buildStandardPaymentLines(rows, {
    isUpdate,
    paymentTypes,
    createKey,
    persistKeys,
    requireBankAccount: true,
  });
}

/** Extract purchase list rows from list API response. */
export function extractPurchaseListRows(res) {
  if (!res?.data) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data.items)) return res.data.items;
  if (Array.isArray(res.data.purchases)) return res.data.purchases;
  return [];
}

/** Normalize list endpoint rows for PurchaseTable. */
export function normalizeListPurchasesRows(res) {
  if (!isRadhaSuccess(res)) return [];

  const rows = extractPurchaseListRows(res);

  return rows.map((row) => {
    const grandTotal =
      row.grand_total ?? row.total_amount ?? row.grandTotal ?? 0;

    return {
      id: row.id,
      purchase_no: row.doc_number || row.purchase_no || "",
      ref_id: row.supplier_id || row.supplierId || "",
      bill_date: row.doc_date || row.bill_date || "",
      status: row.status || "",
      items_count:
        row.item_count ??
        row.total_pcs ??
        row.items_count ??
        row.total_pcs_count ??
        "",
      total_nwt: row.total_nwt ?? row.totalNwt ?? "",
      total_fine_wt:
        row.total_fine_wt ?? row.totalFineWt ?? row.total_fwt ?? "",
      total_amount: String(grandTotal),
      karigar_name: row.supplier_name || row.supplierName || "",
      karigar_company_name:
        row.supplier_company_name || row.supplierCompanyName || "",
      karigar_contact_number: row.supplier_mobile || row.supplierMobile || "",
    };
  });
}

/**
 * Map socket `purchase_created` / `purchase_updated` payload to a PurchaseTable row.
 * Supports legacy `{ purchase_details }` and newERP `{ purchase }` (same field names as list rows).
 */
export function purchaseSocketPayloadToTableRow(payload) {
  if (!payload || typeof payload !== "object") return null;

  if (payload.purchase_details) {
    const d = payload.purchase_details;
    const grandTotal = num(d.total_amount ?? d.final_amount, 0);
    return {
      id: d.id,
      purchase_no: d.purchase_no || "",
      ref_id: d.ref_id || "",
      bill_date: d.bill_date || "",
      status: d.status || "",
      items_count: d.total_pcs ?? "",
      total_nwt: d.total_nwt ?? "",
      total_amount: String(
        grandTotal || d.total_amount || d.final_amount || "",
      ),
      karigar_name: d.karigar_name || "",
      karigar_company_name: d.karigar_company_name || "",
    };
  }

  const doc = payload.purchase;
  if (!doc || typeof doc !== "object") return null;

  const rows = normalizeListPurchasesRows({
    success: true,
    message: "radha",
    data: [doc],
  });
  return rows[0] || null;
}

function pickPurchaseRowFromResponse(res) {
  if (!res) return null;
  if (Array.isArray(res.data)) return res.data[0] || null;
  if (res.data && typeof res.data === "object") return res.data;
  return null;
}

function mapLegacyCharges(charges) {
  if (!Array.isArray(charges)) return [];
  return charges.map((c, idx) => ({
    id: c.id ?? idx + 1,
    charge_type: c.charge_type ?? c.chargeType ?? "",
    chargeType: c.chargeType ?? c.charge_type ?? "",
    pcs: c.pcs ?? 0,
    ct: c.ct ?? 0,
    wt: c.wt ?? c.wtPP ?? c.weight ?? 0,
    wtPP: c.wtPP ?? c.wt ?? c.weight ?? "",
    wtTotal: c.wtTotal ?? "",
    con_percent: c.con_percent ?? c.conPercentage ?? 0,
    conPercentage: c.conPercentage ?? c.con_percent ?? 0,
    rate_on: c.rate_on ?? c.rateOn ?? "",
    rateOn: c.rateOn ?? c.rate_on ?? "",
    rate: c.rate ?? 0,
    amount: c.amount ?? c.amt ?? 0,
  }));
}

/** Convert modular show response row into legacy `purchase_details` for existing edit UI. */
export function getLegacyPurchaseDetailsFromShowResponse(res) {
  if (!isRadhaSuccess(res)) return null;
  const row = pickPurchaseRowFromResponse(res);
  if (!row) return null;

  const itemsIn = Array.isArray(row.items) ? row.items : [];
  const paymentsIn = Array.isArray(row.payments) ? row.payments : [];

  const grandTotal = num(row.grand_total ?? row.total_amount, 0);
  const paid = sumPaymentAmounts(paymentsIn);
  const remaining = Math.max(0, grandTotal - paid);

  const purchaseItems = itemsIn.map((it, idx) => {
    const pcs = num(it.pcs, 0);
    const gwt = num(it.gwt, 0);
    const lwt = num(it.lwt, 0);
    const nwt = num(it.nwt, 0);
    const purity = it.purity ?? it.Purity ?? it.carat_purity ?? 0;
    const wastage = it.wastage ?? it.Wastage ?? 0;

    const fineWtRaw = it.fine_wt ?? it.fineWt ?? "";
    const fineWt =
      fineWtRaw !== ""
        ? num(fineWtRaw, 0)
        : Math.max(0, num(nwt, 0) * (num(purity, 0) / 100) - num(wastage, 0));

    const lbrType = it.lbr_type ?? it.lbrType ?? it.lbrTypeLabel ?? null;

    return {
      id: it.id ?? it.purchase_item_id ?? idx + 1,
      metal_group_name:
        it.product_group_name ?? it.metal_group_name ?? it.product_group ?? "",
      metal_group:
        it.product_group_id ?? it.metal_group_id ?? it.metal_group ?? "",
      Purity: purity,
      wastage,
      pcs,
      gwt,
      lwt,
      nwt,
      fineWt,
      itrateon: it.item_rate_on ?? it.itemRateOn ?? "FWt",
      itrate: num(it.item_rate ?? it.itrate, 0),
      itamt: num(it.item_amount ?? it.itamt ?? it.total_amount, 0),
      lbr_type: lbrType ?? "",
      lbr_percent: num(it.lbr_percent ?? it.lbrPercentage ?? it.lbr_percentage, 0),
      lbr_rate: num(it.lbr_rate ?? it.lbrRate, 0),
      lbr_amt: num(it.lbr_amt ?? it.lbrAmt, 0),
      othamt: num(it.othamt ?? it.other_amount, 0),
      charges: mapLegacyCharges(it.charges),
      mrp: num(it.mrp, 0),
      image: it.image ?? "",
      totalamt: num(it.total_amount ?? it.totalamt, 0),
      // keep modular fields for forward compatibility
      product_id: it.product_id,
      tag_no: it.tag_no,
    };
  });

  const legacy = {
    id: row.id,
    purchase_no: row.doc_number || "",
    bill_type: row.bill_type ?? "",
    bill_type_name: row.bill_type_name ?? "",
    bill_date: row.doc_date ?? "",
    ref_id: row.supplier_id ?? "",
    karigar_name: row.supplier_name ?? "",
    karigar_company_name: row.supplier_company_name ?? "",
    karigar_contact_number: row.supplier_mobile ?? "",

    // Legacy header totals
    final_amount: grandTotal,
    total_amount: grandTotal,
    remaining_amount: String(remaining.toFixed(4)),
    total_pcs: purchaseItems.reduce((s, i) => s + num(i.pcs, 0), 0),
    total_nwt: purchaseItems.reduce((s, i) => s + num(i.nwt, 0), 0),

    discount_amount: num(row.discount_amount ?? row.discount ?? 0, 0),
    gst_percentage: num(row.gst_percent ?? row.tax_percent ?? row.gst_percentage ?? 0, 0),
    other_charges: num(row.other_charges ?? 0, 0),
    round_off: num(row.round_off ?? 0, 0),
    remarks: row.notes ?? row.note ?? "",

    purchase_items: purchaseItems,
    payments: paymentsIn,
  };

  return legacy;
}

export function createPurchasesIdempotencyKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

